import GENESIS_Tools from '@toolproof-core/genesis/_data/timestamped-resources/tools';

import type {
    SpaceOverlayModel,
    SpaceOverlaySection,
} from '@toolproof-core/visualization/runtime/_contract/overlay';

import { resolveBooleanAlgebra } from '@/adapters/primitive/_pure/derivation/boolean/algebra';
import { configForPrimitiveTrace, derivePrimitiveStepsForRuntime, toPrimitiveStepWheels } from '@/adapters/primitive/_pure/derivation/primitiveSteps';
import {
    SECTION_BOOLEAN_INPUT,
    SECTION_BOOLEAN_OPERAND_ONE,
    SECTION_BOOLEAN_OPERAND_TWO,
    SECTION_NATURAL_INPUT,
    SECTION_NATURAL_LESS_THAN_SOURCE,
    SECTION_NATURAL_LESS_THAN_TARGET,
    SECTION_NATURAL_OPERAND_ONE,
    SECTION_NATURAL_OPERAND_TWO,
    getPrimitiveOperationInputError,
    resolvePrimitiveOperationInput,
} from '@/adapters/primitive/_pure/interaction/operationInput';
import type {
    Config,
    PrimitivePresentationOperation,
    RuntimeState,
} from '@/adapters/primitive/_contract/config';
import type { PrimitiveOperationInput } from '@/adapters/primitive/_contract/operation';
import type { PrimitiveStepState } from '@/adapters/primitive/_contract/steps';

export const ACTION_NEXT = 'primitive-next';
export const ACTION_PREV = 'primitive-prev';
export const ACTION_PLAY_PAUSE = 'primitive-play-pause';
export const ACTION_RESET = 'primitive-reset';
export const SECTION_OPERATION = 'primitive-operation';

const TOOL_HANDLE_BY_OPERATION = {
    addition: 'TOOL-NaturalAdd',
    subtraction: 'TOOL-NaturalSubtract',
    multiplication: 'TOOL-NaturalMultiply',
    division: 'TOOL-NaturalDivide',
    'natural-identity': 'TOOL-NaturalIdentity',
    'boolean-identity': 'TOOL-BooleanIdentity',
    'logical-and': 'TOOL-LogicalAnd',
    'logical-nand': 'TOOL-LogicalNAnd',
    'logical-xor': 'TOOL-LogicalXOr',
    'logical-xnor': 'TOOL-LogicalXNor',
    'logical-or': 'TOOL-LogicalOr',
    'logical-nor': 'TOOL-LogicalNOr',
    'logical-not': 'TOOL-LogicalNot',
    'natural-less-than': 'TOOL-NaturalLessThan',
} as const satisfies Record<PrimitivePresentationOperation, string>;

const OPERATION_OPTIONS = Object.entries(TOOL_HANDLE_BY_OPERATION).map(([value, handle]) => {
    const tool = GENESIS_Tools.find((resource) => resource.projection.handle === handle)?.projection;
    if (!tool) throw new Error(`Primitive operation '${value}' requires Genesis tool '${handle}'.`);
    return { value, label: tool.name };
});

export function isPrimitiveOperation(value: string): value is PrimitivePresentationOperation {
    return OPERATION_OPTIONS.some((option) => option.value === value);
}

function toOperationTitle(operation: PrimitivePresentationOperation): string {
    const option = OPERATION_OPTIONS.find((option) => option.value === operation);
    if (!option) throw new Error(`Unknown Primitive operation '${operation}'.`);
    return option.label;
}

export function toOperationSummary(input: PrimitiveOperationInput | null): string {
    if (input === null) return 'Enter values to begin.';
    switch (input.operation) {
        case 'addition':
            return `${input.operandOne} + ${input.operandTwo}`;
        case 'subtraction':
            return `${input.operandOne} - ${input.operandTwo}`;
        case 'multiplication':
            return `${input.operandOne} × ${input.operandTwo}`;
        case 'division':
            return `${input.operandOne} ÷ ${input.operandTwo}`;
        case 'natural-identity':
            return `id(${input.input})`;
        case 'boolean-identity':
            return `id(${input.input})`;
        case 'logical-not':
            return `NOT ${input.input}`;
        case 'logical-and':
        case 'logical-nand':
        case 'logical-xor':
        case 'logical-xnor':
        case 'logical-or':
        case 'logical-nor':
            return `${input.operandOne} ${input.operation.slice('logical-'.length).replace('-', '').toUpperCase()} ${input.operandTwo}`;
        case 'natural-less-than':
            return `${input.source} < ${input.target}`;
    }
}

function toCompletedEquation(
    config: Config,
    input: PrimitiveOperationInput,
    steps: readonly PrimitiveStepState[],
): string {
    const wheelValue = (role: 'product' | 'quotient' | 'remainder') => {
        const wheel = toPrimitiveStepWheels(steps.at(-1)!).find((candidate) => candidate.role === role);
        if (wheel === undefined) throw new Error(`Completed ${input.operation} is missing ${role}.`);
        return wheel.powers.reduce(
            (value, power) => value + (wheel.countedThroughDigitByPower?.[power] ?? 0) * config.natural.radix ** power,
            0,
        );
    };

    switch (input.operation) {
        case 'addition':
            return `${input.operandOne} + ${input.operandTwo} = ${input.operandOne + input.operandTwo}.`;
        case 'subtraction':
            return `${input.operandOne} - ${input.operandTwo} = ${input.operandOne - input.operandTwo}.`;
        case 'multiplication':
            return `${input.operandOne} × ${input.operandTwo} = ${wheelValue('product')}.`;
        case 'division':
            return `${input.operandOne} ÷ ${input.operandTwo} = ${wheelValue('quotient')} rem ${wheelValue('remainder')}.`;
        case 'natural-identity':
            return `id(${input.input}) = ${input.input}.`;
        case 'boolean-identity':
            return `id(${input.input}) = ${input.input}.`;
        case 'logical-not':
            return `NOT ${input.input} = ${resolveBooleanAlgebra({ operation: input.operation, inputs: [input.input] }).value}.`;
        case 'logical-and':
        case 'logical-nand':
        case 'logical-xor':
        case 'logical-xnor':
        case 'logical-or':
        case 'logical-nor':
            return `${toOperationSummary(input)} = ${resolveBooleanAlgebra({
                operation: input.operation,
                inputs: [input.operandOne, input.operandTwo],
            }).value}.`;
        case 'natural-less-than':
            return `${input.source} < ${input.target} = ${input.source < input.target}.`;
    }
}

function toOperandSections(runtimeState: RuntimeState): SpaceOverlaySection[] {
    const { inputDraft } = runtimeState;
    const booleanSelect = (sectionId: string, label: string, selectedValue: '' | 'true' | 'false'): SpaceOverlaySection => ({
        sectionKind: 'select',
        sectionId,
        label,
        selectedValue,
        options: [
            { value: '', label: 'Select value' },
            { value: 'true', label: 'true' },
            { value: 'false', label: 'false' },
        ],
    });
    const textInput = (sectionId: string, label: string, value: string, description: string): SpaceOverlaySection => ({
        sectionKind: 'text-input',
        sectionId,
        label,
        value,
        description,
        enabled: true,
        errorMessage: getPrimitiveOperationInputError(inputDraft, sectionId),
    });

    switch (inputDraft.operation) {
        case 'addition':
            return [
                textInput(SECTION_NATURAL_OPERAND_ONE, 'Addend One', inputDraft.operandOneText, 'Up to three digits'),
                textInput(SECTION_NATURAL_OPERAND_TWO, 'Addend Two', inputDraft.operandTwoText, 'Up to three digits'),
            ];
        case 'subtraction':
            return [
                textInput(SECTION_NATURAL_OPERAND_ONE, 'Minuend', inputDraft.operandOneText, 'Up to three digits'),
                textInput(SECTION_NATURAL_OPERAND_TWO, 'Subtrahend', inputDraft.operandTwoText, 'Up to three digits'),
            ];
        case 'multiplication':
            return [
                textInput(SECTION_NATURAL_OPERAND_ONE, 'Multiplier', inputDraft.operandOneText, 'Up to two digits'),
                textInput(SECTION_NATURAL_OPERAND_TWO, 'Multiplicand', inputDraft.operandTwoText, 'Up to two digits'),
            ];
        case 'division':
            return [
                textInput(SECTION_NATURAL_OPERAND_ONE, 'Dividend', inputDraft.operandOneText, 'Up to two digits'),
                textInput(SECTION_NATURAL_OPERAND_TWO, 'Divisor', inputDraft.operandTwoText, 'One or two digits; zero is allowed for the exotic terminal-state visualization.'),
            ];
        case 'natural-identity':
            return [textInput(SECTION_NATURAL_INPUT, 'Input', inputDraft.inputText, 'Up to three digits')];
        case 'boolean-identity':
            return [booleanSelect(SECTION_BOOLEAN_INPUT, 'Input', inputDraft.inputText)];
        case 'logical-not':
            return [booleanSelect(SECTION_BOOLEAN_INPUT, 'Operand', inputDraft.inputText)];
        case 'logical-and':
        case 'logical-nand':
        case 'logical-xor':
        case 'logical-xnor':
        case 'logical-or':
        case 'logical-nor':
            return [
                booleanSelect(SECTION_BOOLEAN_OPERAND_ONE, 'Operand One', inputDraft.operandOneText),
                booleanSelect(SECTION_BOOLEAN_OPERAND_TWO, 'Operand Two', inputDraft.operandTwoText),
            ];
        case 'natural-less-than':
            return [
                textInput(SECTION_NATURAL_LESS_THAN_SOURCE, 'Source', inputDraft.sourceText, 'Up to three digits'),
                textInput(SECTION_NATURAL_LESS_THAN_TARGET, 'Target', inputDraft.targetText, 'Up to three digits'),
            ];
    }
}

export function buildPrimitiveOverlayModel(args: {
    config: Config;
    runtimeState: RuntimeState;
    steps?: readonly PrimitiveStepState[];
}): SpaceOverlayModel {
    const { runtimeState } = args;
    const config = configForPrimitiveTrace(args.config, runtimeState.selectedTrace);
    const input = runtimeState.selectedTrace?.payload.input ?? resolvePrimitiveOperationInput(runtimeState.inputDraft);
    const steps = args.steps ?? derivePrimitiveStepsForRuntime(config, runtimeState);
    const maxStepIndex = Math.max(0, steps.length - 1);
    const activeStepIndex = runtimeState.pendingStepIndex ?? runtimeState.committedStepIndex;
    const activeStep = steps[activeStepIndex] ?? steps[0];
    const isTransitioning = runtimeState.pendingStepIndex !== null || runtimeState.booleanAssignment !== undefined;
    const isComplete = input !== null && !isTransitioning && runtimeState.committedStepIndex === maxStepIndex;
    const canAdvance = input !== null && runtimeState.committedStepIndex < maxStepIndex && !isTransitioning;
    const canGoBack = input !== null && (runtimeState.committedStepIndex > 0 || runtimeState.pendingStepIndex !== null);

    return {
        overlayId: `primitive-operation-${runtimeState.operation}`,
        title: toOperationTitle(runtimeState.operation),
        sections: [
            {
                sectionKind: 'select',
                sectionId: SECTION_OPERATION,
                label: 'Operation',
                selectedValue: runtimeState.operation,
                options: [...OPERATION_OPTIONS],
            } satisfies SpaceOverlaySection, ...toOperandSections(runtimeState),
            {
                sectionKind: 'actions',
                sectionId: 'primitive-playback',
                label: 'Playback',
                description: isComplete ? 'Complete' : input === null ? 'Awaiting valid values' : 'Ready',
                actions: [
                    {
                        actionId: ACTION_PLAY_PAUSE,
                        playbackRole: 'toggle',
                        label: runtimeState.isPlaying ? 'Pause' : 'Play',
                        enabled: canAdvance || isTransitioning || runtimeState.isPlaying,
                        isActive: runtimeState.isPlaying,
                    },
                    { actionId: ACTION_RESET, playbackRole: 'reset', label: 'Reset', enabled: runtimeState.committedStepIndex > 0 || isTransitioning },
                    { actionId: ACTION_PREV, playbackRole: 'step', label: 'Prev', enabled: canGoBack && !isTransitioning && !runtimeState.isPlaying },
                    { actionId: ACTION_NEXT, playbackRole: 'step', label: 'Next', enabled: canAdvance && !runtimeState.isPlaying },
                ],
            },
            {
                sectionKind: 'details',
                sectionId: 'primitive-status',
                label: 'State',
                rows: [
                    { key: 'Expression', value: toOperationSummary(input) },
                    { key: 'Step', value: `${activeStepIndex} / ${maxStepIndex}` },
                    { key: 'Phase', value: runtimeState.booleanAssignment !== undefined ? 'Assign Input' : input === null ? 'Awaiting input' : isComplete ? 'Complete' : activeStep?.label ?? 'Unknown' },
                    { key: 'Description', value: runtimeState.booleanAssignment !== undefined ? 'Materializing the assigned value.' : input === null ? 'Enter valid operands to enable playback.' : isComplete ? toCompletedEquation(config, input, steps) : activeStep?.description ?? '' },
                ],
            },
        ],
    };
}